<script>
import { fileToBase64 } from '../../../_default/utils/fileToBase64'
import { retiradaEmMassa } from '../../../../domain/automacoes/services'

const EXTENSOES_PERMITIDAS = ['xlsx', 'xls']
const TAMANHO_MAXIMO_BYTES = 5 * 1024 * 1024 // 5MB, mesmo limite validado na API

export default {
  name: 'RetiradaEmMassa',
  data () {
    return {
      arquivo: null,
      arquivoBase64: null,
      dadosPrimeiraLinha: false,
      loading: false,
      resultado: null // resposta da API (preview ou aplicado)
    }
  },
  computed: {
    temPreview () {
      return !!this.resultado && !this.resultado.aplicado
    },
    temResultadoAplicado () {
      return !!this.resultado && this.resultado.aplicado
    },
    podeConfirmar () {
      return this.temPreview && this.resultado.totalAtualizadas > 0
    }
  },
  methods: {
    extensao (nome) {
      return (nome.split('.').pop() || '').toLowerCase()
    },
    escolherArquivo () {
      if (this.loading) return
      this.$refs.inputArquivo.click()
    },
    async onFileChange (e) {
      const file = e.target.files[0]
      this.limparInput()
      if (!file) return

      if (!EXTENSOES_PERMITIDAS.includes(this.extensao(file.name))) {
        alert('Formato de arquivo inválido. Envie um .xlsx ou .xls.')
        return
      }
      if (file.size > TAMANHO_MAXIMO_BYTES) {
        alert('Arquivo muito grande. Limite de 5MB.')
        return
      }

      this.resultado = null
      this.arquivo = file
      this.arquivoBase64 = await fileToBase64(file)
    },
    limparInput () {
      if (this.$refs.inputArquivo) this.$refs.inputArquivo.value = ''
    },
    trocarArquivo () {
      this.arquivo = null
      this.arquivoBase64 = null
      this.resultado = null
    },
    async analisar () {
      await this.enviar(false)
    },
    async confirmar () {
      if (!this.podeConfirmar) return
      const confirma = confirm(
        `Confirma a atualização de ${this.resultado.totalAtualizadas} retirada(s)? Esta ação não pode ser desfeita automaticamente.`
      )
      if (!confirma) return
      await this.enviar(true)
    },
    async enviar (aplicar) {
      if (!this.arquivoBase64 || this.loading) return
      this.loading = true
      this.$loaderOverlay.open().message(aplicar ? 'Aplicando alterações...' : 'Analisando planilha...')
      try {
        const { data } = await retiradaEmMassa({
          filename: this.arquivo.name,
          data: this.arquivoBase64,
          dadosPrimeiraLinha: this.dadosPrimeiraLinha,
          aplicar
        })
        this.resultado = data
        if (aplicar) {
          this.$emit('finishSubmit')
        }
      } catch (response) {
        window.responseError(response)
        console.log(response)
      } finally {
        this.loading = false
        this.$loaderOverlay.hide()
      }
    },
    corStatus (status) {
      if (status === 'ok') return 'text-success'
      if (status === 'erro') return 'text-danger'
      return 'text-muted'
    },
    iconeStatus (status) {
      if (status === 'ok') return 'fa-check'
      if (status === 'erro') return 'fa-times'
      return 'fa-minus'
    }
  }
}
</script>

<template>
  <div class="wrapper-md">
    <p class="text-muted">
      Envie uma planilha (.xlsx ou .xls) para corrigir o <strong>nome</strong>, <strong>tipo/número de documento</strong>,
      <strong>CEP</strong> e <strong>endereço</strong> já cadastrados na retirada de várias GRVs de uma só vez.
      A planilha deve ter uma linha de cabeçalho com as colunas:
    </p>
    <ul class="text-muted">
      <li><strong>GRV</strong> (ou <strong>numero</strong>) — obrigatória, número da GRV</li>
      <li><strong>NOME</strong> — nome de quem retirou/arrematou</li>
      <li><strong>TIPO DOCUMENTO</strong> — CPF ou CNPJ</li>
      <li><strong>NUMERO DOCUMENTO</strong> — número do CPF/CNPJ</li>
      <li><strong>CEP</strong></li>
      <li><strong>Endereço</strong></li>
    </ul>
    <p class="text-muted">
      A GRV precisa já ter uma retirada cadastrada — esta automação não cria retiradas novas, só corrige os dados
      de quem retirou. Limite de 5MB e 2000 linhas por envio.
    </p>

    <div class="checkbox m-b" v-if="!resultado">
      <label>
        <input type="checkbox" v-model="dadosPrimeiraLinha" :disabled="loading">
        É o mesmo arrematante em toda a planilha — usar os dados da <strong>primeira linha</strong> em todas as GRVs
      </label>
    </div>

    <div class="m-b" v-if="!arquivo">
      <button type="button" class="btn btn-default" @click="escolherArquivo" :disabled="loading">
        <i class="fa fa-upload m-r-xs"></i> Selecionar planilha
      </button>
      <input
        type="file"
        ref="inputArquivo"
        v-show="false"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        @change="onFileChange"
      >
    </div>

    <div class="m-b" v-else>
      <span><i class="fa fa-file-excel-o m-r-xs"></i> {{ arquivo.name }}</span>
      <button type="button" class="btn btn-xs btn-link" @click="trocarArquivo" :disabled="loading">
        (trocar arquivo)
      </button>
      <div class="m-t-sm" v-if="!resultado">
        <button type="button" class="btn btn-primary" @click="analisar" :disabled="loading">
          <i class="fa fa-search m-r-xs"></i> Analisar planilha (pré-visualização)
        </button>
      </div>
    </div>

    <div v-if="resultado">
      <div class="alert" :class="temResultadoAplicado ? 'alert-success' : 'alert-info'">
        <strong>{{ temResultadoAplicado ? 'Alterações aplicadas.' : 'Pré-visualização (nada foi gravado ainda).' }}</strong>
        <br>
        Total de linhas: {{ resultado.totalLinhas }} —
        <span class="text-success">{{ resultado.totalAtualizadas }} para atualizar</span> —
        <span class="text-danger">{{ resultado.totalErros }} com erro</span>
      </div>

      <div class="table-responsive" style="max-height: 45vh; overflow-y: auto;">
        <table class="table table-hover table-striped table-condensed b-t b-light">
          <thead>
          <tr>
            <th>Linha</th>
            <th>GRV</th>
            <th>Status</th>
            <th>Mensagem</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="linha in resultado.linhas" :key="linha.linha">
            <td>{{ linha.linha }}</td>
            <td>{{ linha.numero }}</td>
            <td :class="corStatus(linha.status)">
              <i class="fa" :class="iconeStatus(linha.status)"></i> {{ linha.status }}
            </td>
            <td>{{ linha.mensagem }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="m-t" v-if="temPreview">
        <button type="button" class="btn btn-success" @click="confirmar" :disabled="!podeConfirmar || loading">
          <i class="fa fa-check m-r-xs"></i> Confirmar e aplicar {{ resultado.totalAtualizadas }} alteração(ões)
        </button>
        <span class="text-muted m-l-sm" v-if="!podeConfirmar">Nenhuma linha válida para aplicar.</span>
      </div>

      <div class="m-t" v-if="temResultadoAplicado">
        <button type="button" class="btn btn-default" @click="$emit('close')">Fechar</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>



// WEBPACK FOOTER //
// RetiradaEmMassa.vue?377a9468